import moment from 'moment'
import { getCurrentLanguage } from 'locale'

import { SCHEDULE_TYPES } from 'constants'
import { isTodayDay } from './datetime'

export const isAnyScheduleOverridden = playday => playday.schedule_items.some(hasOverriddenSchedule)

export function getScheduleItemsByType(playday, type) {
    return playday.schedule_items.filter(schedule_item => schedule_item.type === type)
}

export function hasOverriddenSchedule(schedule_item) {
    return schedule_item.schedules.some(schedule => schedule.overridden)
}

export const isScheduleDisabled = schedule => schedule.enabled === false
export const isScheduleDisabledOrOverridden = schedule => (
    schedule.enabled === false ||
    (schedule.enabled === true && schedule.overridden === true)
)

export const allSchedulesDisabled = scheduleItem => scheduleItem.schedules.every(isScheduleDisabled)

export const getDisabledScheduleItems = playday => playday.schedule_items.filter(allSchedulesDisabled)

export function hasChallenges(playday) {
    return playday.schedule_items.some(scheduleItem => scheduleItem.type === SCHEDULE_TYPES.CHALLENGES)
}

export const scheduleOpenThrottler = {
    timeoutId: null,
    throttle: function(callback, timeout = 5000) {
        let isTimeoutSet = this.timeoutId !== null

        clearTimeout(this.timeoutId)
        this.timeoutId = setTimeout(() => {
            this.timeoutId = null
        }, timeout)

        if (!isTimeoutSet) callback()
    },
}

/**
 * Check that currentPeriod is the first within playday
 * @param {int[]} currentPeriod - Current period, example: [5, 6]
 * @param {int[][]} periods - All prime time periods for this play day
 * @param {int} playdayStart - Hour of play day start
 * @param {int} playdayEnd - Last of play day
 *
 * Note that playdayStart can be lower than playdayEnd if play day starts, for example, at evening
 * (UTC) and ends at morning of the next day (UTC). Explain that we have prime time start ane end:
 * 1:00 ... 22:00 (Pacific time) = 8:00 - 5:00 (UTC) -> belongs to the next calendar day!.
 * and prime time periods: [[1, 2], [3, 4], [10, 11]]
 * In this case the first period at playday will be [10, 11].
 * */
export const checkIsFirstPeriod = (currentPeriod, periods, playdayStart, playdayEnd) => {
    if (!timeInsidePeriod(currentPeriod[0], playdayStart, playdayEnd)) return false
    const firstPeriodAtPlayday = [...periods]  // preventing change of 'periods' in place in .sort
        .sort((periodA, periodB) => periodA[0] - [periodB[0]])
        .find(period => period[0] >= playdayStart) || periods[0]

    const firstPrimetimeAtPlaydayStart = firstPeriodAtPlayday && firstPeriodAtPlayday[0]
    return currentPeriod[0] === firstPrimetimeAtPlaydayStart
}

export function getFirstBattleHour(periods, playdayStart, playdayEnd) {
    const first = periods.find(period => checkIsFirstPeriod(period, periods, playdayStart, playdayEnd))
    return first ? first[0] : null
}

export const getClosestPrimetimeStart = (
    playdays, playdayStart, playdayEnd, battleType,
    currentHour = moment.utc().hour()
) => {
    for (let day of playdays) {
        let periods = []
        for (let item of day.schedule_items) {
            if (item.type !== SCHEDULE_TYPES.CHALLENGES && checkScheduleType(item, battleType)) {
                for (let schedule of item.schedules) {
                    if (schedule.enabled && schedule.periods.length) {
                        periods = periods.concat(schedule.periods)
                    }
                }
            }
        }
        if (periods.length) {
            let date = day.date
            let firstHour

            if (isTodayDay(moment(date))) firstHour = getFirstBattleHour(periods, currentHour, playdayEnd)
            else firstHour = getFirstBattleHour(periods, playdayStart, playdayEnd)

            if (firstHour !== null) {
                date.setUTCHours(firstHour, 0, 0, 0)
                return moment.utc(date)
            }
        }
    }
}

export const checkScheduleType = (scheduleItem, battleType) => (
    !battleType || battleType === scheduleItem.type
)

export const vehicleLevelFilter = (scheduleItem, vehicleLevel) => (
    vehicleLevel === null ||
    vehicleLevel === undefined ||
    scheduleItem.vehicle_level === vehicleLevel
)

export function checkTimeInsidePlayday(time, playdayDate, startHour, endHour) {
    const startTime = moment.utc(playdayDate).hour(startHour).startOf('hour')
    const endTime = moment.utc(playdayDate).hour(endHour).startOf('hour')

    if (endTime < startTime) {
        endTime.add(1, 'days')
    }

    return time.isBetween(startTime, endTime)
}

// for test purposes: impossible to mock time zone in moment.local() calls
export const momentToLocal = momentTime => momentTime.local()
export const getCurrentLanguageFromLocale = () => getCurrentLanguage()

function timeInsidePeriod(time, start, end) {
    if (start < end) return start <= time && time < end
    return start <= time || time < end
}

export function fortBattleStartedLessThanHourAgo(playday) {
    if (!playday || !playday.schedule_items) return false

    const nowUtcHour = moment.utc().hour()
    const fortBattlesScheduleItem = playday.schedule_items.find(
        item => item.type === SCHEDULE_TYPES.FORT_BATTLE
    )

    if (!fortBattlesScheduleItem || !fortBattlesScheduleItem.schedules) return false

    return fortBattlesScheduleItem.schedules.some(
        schedule => schedulePeriodStartsAtHour(schedule, nowUtcHour)
    )
}

function schedulePeriodStartsAtHour(schedule, hour) {
    if (!schedule.enabled || !schedule.periods) return false

    return schedule.periods.some(periods => {
        const primetimeStart = periods[0]
        return primetimeStart === hour
    })
}

export const clearScheduleItemsOfChallenges = schedule_items => (
    schedule_items.filter(scheduleItem => scheduleItem.type !== SCHEDULE_TYPES.CHALLENGES)
)

export function initData(playdays) {
    return playdays.map(playday => ({ ...playday, date: new Date(playday.date) }))
}



// WEBPACK FOOTER //
// ./helpers/schedule.js